<template>
  <AppPanelModal 
    :is-open="isOpen" 
    @didDismiss="$emit('close')"
    @willPresent="handleWillPresent"
    @didPresent="handleDidPresent"
  >
    <ion-header>
      <ion-toolbar class="bg-gradient-to-r from-blue-500 to-indigo-600">
        <ion-title class="text-white font-bold">
          <div class="flex items-center gap-2">
            <span class="text-2xl">{{ stop ? '✏️' : '📍' }}</span>
            {{ stop ? t('editStop') : t('addStop') }}
          </div>
        </ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')" class="text-white">
            <ion-icon :icon="closeOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-900/10 dark:to-indigo-900/10" :key="language">
      <form @submit.prevent="handleSubmit" class="space-y-4 max-w-2xl mx-auto">
        <!-- Address with Autocomplete -->
        <div class="relative">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <span>🔍</span> {{ t('address') }} *
          </label>
          <input
            v-model="searchQuery"
            @input="handleAddressSearch"
            @focus="showSuggestions = true"
            type="text"
            required
            class="input-field"
            :placeholder="t('searchAddress')"
          />
          
          <!-- Autocomplete Suggestions -->
          <div
            v-if="showSuggestions && suggestions.length > 0"
            class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border-2 border-blue-200 dark:border-blue-700 max-h-60 overflow-y-auto"
          >
            <button
              v-for="(suggestion, index) in suggestions"
              :key="index"
              type="button"
              @click="selectSuggestion(suggestion)"
              class="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
            >
              <div class="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span>📍</span> {{ suggestion.name }}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {{ suggestion.address }}
              </div>
              <div class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                📍 {{ suggestion.lat.toFixed(4) }}, {{ suggestion.lng.toFixed(4) }}
              </div>
            </button>
          </div>
          
          <!-- Loading indicator -->
          <div
            v-if="isSearching"
            class="absolute right-3 top-9 text-blue-500"
          >
            <span class="loading-spinner"></span>
          </div>
        </div>

        <!-- Stop Name -->
        <div>
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <span>🏷️</span> {{ t('stopName') }} *
          </label>
          <input
            v-model="form.name"
            type="text"
            required
            class="input-field"
            :placeholder="t('enterStopName')"
          />
        </div>

        <!-- Coordinates with Map Helper -->
        <div class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border-2 border-green-200 dark:border-green-700">
          <div class="flex items-center justify-between mb-3">
            <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <span>🌍</span> {{ t('coordinates') }} *
            </label>
            <button
              type="button"
              @click="useCurrentMapCenter"
              class="text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 bg-white/70 dark:bg-gray-800/70 px-3 py-1 rounded-full transition-all"
            >
              📍 {{ t('useMapCenter') }}
            </button>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
                <span>📐</span> {{ t('latitude') }}
              </label>
              <input
                v-model.number="form.lat"
                type="number"
                step="any"
                required
                class="input-field"
                placeholder="47.5"
                readonly
              />
            </div>
            <div>
              <label class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">
                <span>📐</span> {{ t('longitude') }}
              </label>
              <input
                v-model.number="form.lng"
                type="number"
                step="any"
                required
                class="input-field"
                placeholder="19.0"
                readonly
              />
            </div>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
            <span>💡</span> {{ t('coordinatesAutoFilled') }}
          </p>
        </div>

        <!-- Stop Type -->
        <div class="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border-2 border-purple-200 dark:border-purple-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <span>🏷️</span> {{ t('stopType') }} *
          </label>
          <select v-model="form.type" class="input-field" required>
            <option value="">{{ t('selectType') }}</option>
            <optgroup label="🏙️ General">
              <option value="city">🏙️ {{ t('city') }}</option>
              <option value="parking">🅿️ {{ t('parking') }}</option>
              <option value="rest_area">🚻 {{ t('restArea') }}</option>
              <option value="other">📍 {{ t('other') }}</option>
            </optgroup>
            <optgroup label="⛽ Fuel & Charging">
              <option value="gas_station">⛽ {{ t('gasStation') }}</option>
              <option value="ev_charging">⚡ {{ t('evCharging') }}</option>
            </optgroup>
            <optgroup label="🍽️ Food & Drink">
              <option value="restaurant">�️ {{ t('restaurant') }}</option>
              <option value="fast_food">🍔 {{ t('fastFood') }}</option>
              <option value="cafe">☕ {{ t('cafe') }}</option>
            </optgroup>
            <optgroup label="🏨 Accommodation">
              <option value="hotel">� {{ t('hotel') }}</option>
              <option value="motel">🛏️ {{ t('motel') }}</option>
              <option value="camping">⛺ {{ t('camping') }}</option>
            </optgroup>
            <optgroup label="🎯 Attractions">
              <option value="attraction">🎯 {{ t('attraction') }}</option>
              <option value="museum">🏛️ {{ t('museum') }}</option>
              <option value="park">🌳 {{ t('park') }}</option>
              <option value="viewpoint">🏔️ {{ t('viewpoint') }}</option>
              <option value="shopping">🛍️ {{ t('shopping') }}</option>
            </optgroup>
          </select>
        </div>

        <!-- Duration -->
        <div class="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg p-4 border-2 border-orange-200 dark:border-orange-700">
          <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <span>⏱️</span> {{ t('plannedDuration') }}
          </label>
          <input
            v-model.number="form.duration"
            type="number"
            min="0"
            class="input-field"
            placeholder="60"
          />
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
            <span>💡</span> {{ t('howLongToStay') }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 pt-4">
          <button
            type="button"
            @click="$emit('close')"
            class="btn-secondary flex-1 font-semibold"
          >
            ❌ {{ t('cancel') }}
          </button>
          <button
            type="submit"
            class="btn-primary flex-1 font-semibold shadow-lg"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="loading-spinner mr-2"></span>
            {{ stop ? '💾 ' + t('updateStop') : '➕ ' + t('addStop') }}
          </button>
        </div>
      </form>
    </ion-content>
  </AppPanelModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent
} from '@ionic/vue'
import { closeOutline } from 'ionicons/icons'
import type { Stop, StopType } from '@/types'
import AppPanelModal from '@/components/AppPanelModal.vue'
import { searchAddress, debounce, reverseGeocode, type GeocodingResult } from '@/services/geocoding'
import { useMapStore } from '@/stores/map'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import { storeToRefs } from 'pinia'

interface Props {
  isOpen: boolean
  stop?: Stop | null
  defaultLat?: number
  defaultLng?: number
}

const props = withDefaults(defineProps<Props>(), {
  stop: null,
  defaultLat: 47.5,
  defaultLng: 19.0
})

const emit = defineEmits<{
  close: []
  save: [stopData: Omit<Stop, 'id' | 'createdAt' | 'updatedAt'>]
}>()

const mapStore = useMapStore()
const { t } = useI18n()
const settingsStore = useSettingsStore()
const { language } = storeToRefs(settingsStore)

const isLoading = ref(false)
const isSearching = ref(false)
const searchQuery = ref('')
const suggestions = ref<GeocodingResult[]>([])
const showSuggestions = ref(false)

const form = ref({
  name: '',
  address: '',
  type: '' as StopType | '',
  lat: props.defaultLat,
  lng: props.defaultLng,
  duration: 60,
  order: 0
})

// Use current map center as coordinates
async function useCurrentMapCenter() {
  const [lat, lng] = mapStore.center
  form.value.lat = lat
  form.value.lng = lng
  
  // Try to get address from coordinates
  try {
    const address = await reverseGeocode(lat, lng)
    form.value.address = address
    searchQuery.value = address
  } catch (error) {
    console.error('Reverse geocoding error:', error)
  }
}

// Debounced search function
const debouncedSearch = debounce(async (query: string) => {
  if (!query || query.length < 3) {
    suggestions.value = []
    isSearching.value = false
    return
  }

  isSearching.value = true
  try {
    const results = await searchAddress(query)
    suggestions.value = results
  } catch (error) {
    console.error('Address search error:', error)
    suggestions.value = []
  } finally {
    isSearching.value = false
  }
}, 500)

// Handle address search input
function handleAddressSearch() {
  // Sync form.address with searchQuery
  form.value.address = searchQuery.value
  debouncedSearch(searchQuery.value)
}

// Detect stop type from place name and type
function detectStopType(suggestion: GeocodingResult): StopType | '' {
  const name = suggestion.name.toLowerCase()
  const type = suggestion.type?.toLowerCase() || ''
  
  // Fuel & Charging
  if (type.includes('fuel') || type.includes('gas') || name.includes('benzin') || 
      name.includes('shell') || name.includes('mol') || name.includes('omv') || 
      name.includes('esso') || name.includes('bp ') || name.includes('lukoil') ||
      name.includes('petrol') || name.includes('gas station')) {
    return 'gas_station'
  }
  if (type.includes('charging') || name.includes('töltő') || name.includes('charging') ||
      name.includes('tesla') || name.includes('e-mobi') || name.includes('ionity')) {
    return 'ev_charging'
  }
  
  // Food & Drink
  if (type.includes('restaurant') || name.includes('étterem') || name.includes('restaurant') ||
      name.includes('vendéglő') || name.includes('pizz') || name.includes('steakhouse')) {
    return 'restaurant'
  }
  if (type.includes('fast_food') || name.includes('mcdonald') || name.includes('burger king') ||
      name.includes('kfc') || name.includes('subway') || name.includes('fast food')) {
    return 'fast_food'
  }
  if (type.includes('cafe') || type.includes('coffee') || name.includes('kávé') || 
      name.includes('cafe') || name.includes('coffee') || name.includes('espresso') ||
      name.includes('starbucks') || name.includes('costa')) {
    return 'cafe'
  }
  
  // Accommodation
  if (type.includes('hotel') || name.includes('hotel') || name.includes('szálloda')) {
    return 'hotel'
  }
  if (type.includes('motel') || name.includes('motel')) {
    return 'motel'
  }
  if (type.includes('camp') || name.includes('kemping') || name.includes('camping')) {
    return 'camping'
  }
  
  // Attractions
  if (type.includes('museum') || name.includes('múzeum') || name.includes('museum') ||
      name.includes('galéria') || name.includes('gallery')) {
    return 'museum'
  }
  if (type.includes('park') || name.includes('park')) {
    return 'park'
  }
  if (type.includes('viewpoint') || name.includes('kilátó') || name.includes('lookout')) {
    return 'viewpoint'
  }
  if (type.includes('shop') || name.includes('bevásárló') || name.includes('mall') ||
      name.includes('plaza') || name.includes('áruház')) {
    return 'shopping'
  }
  if (type.includes('attraction') || type.includes('tourist') || name.includes('vár') ||
      name.includes('castle') || name.includes('templom') || name.includes('church')) {
    return 'attraction'
  }
  
  // General
  if (type.includes('parking') || name.includes('parkoló') || name.includes('parking')) {
    return 'parking'
  }
  if (type.includes('rest') || name.includes('pihenő') || name.includes('rest area')) {
    return 'rest_area'
  }
  if (type.includes('city') || type.includes('town') || type.includes('place')) {
    return 'city'
  }
  
  // Default: empty (user will select)
  return ''
}

// Select a suggestion
function selectSuggestion(suggestion: GeocodingResult) {
  form.value.address = suggestion.address
  form.value.lat = suggestion.lat
  form.value.lng = suggestion.lng
  
  // If name is empty, use the suggestion name
  if (!form.value.name) {
    form.value.name = suggestion.name
  }
  
  // Auto-detect stop type from place name and type
  const detectedType = detectStopType(suggestion)
  if (detectedType && !form.value.type) {
    form.value.type = detectedType
    console.log('✅ Auto-detected stop type:', detectedType)
  }
  
  searchQuery.value = suggestion.address
  showSuggestions.value = false
  suggestions.value = []
  
  console.log('✅ Suggestion selected:', suggestion)
  console.log('✅ Form updated:', form.value)
}

// Close suggestions when clicking outside
function closeSuggestions() {
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Handle modal presentation events
function handleWillPresent() {
  console.log('🎭 Modal will present')
  // Remove focus from any active element
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
}

function handleDidPresent() {
  console.log('🎭 Modal did present')
  // Focus the first input field
  setTimeout(() => {
    const firstInput = document.querySelector('.ion-page:not(.ion-page-hidden) input')
    if (firstInput instanceof HTMLElement) {
      firstInput.focus()
    }
  }, 100)
}

// Watch for prop changes to populate form
watch(() => props.stop, (newStop) => {
  if (newStop) {
    form.value = {
      name: newStop.name,
      address: newStop.address,
      type: newStop.type,
      lat: newStop.lat,
      lng: newStop.lng,
      duration: newStop.duration,
      order: newStop.order
    }
    searchQuery.value = newStop.address
  }
}, { immediate: true })

watch(() => props.isOpen, (isOpen) => {
  console.log('👁️ Modal isOpen changed:', isOpen)
  if (isOpen && !props.stop) {
    // Reset form for new stop
    form.value = {
      name: '',
      address: '',
      type: '' as StopType | '',
      lat: props.defaultLat,
      lng: props.defaultLng,
      duration: 60,
      order: 0
    }
    searchQuery.value = ''
    suggestions.value = []
    showSuggestions.value = false
    console.log('🔄 Form reset for new stop')
  }
})

async function handleSubmit() {
  console.log('🎯 AddStopModal: handleSubmit called')
  console.log('🎯 form values:', form.value)
  
  // Validation
  if (!form.value.name) {
    alert('Please enter a stop name')
    console.warn('⚠️ No name entered!')
    return
  }
  
  if (!form.value.address) {
    alert('Please enter an address')
    console.warn('⚠️ No address entered!')
    return
  }
  
  if (!form.value.type) {
    alert('Please select a stop type')
    console.warn('⚠️ No type selected!')
    return
  }
  
  if (!form.value.lat || !form.value.lng) {
    alert('Please select an address from the suggestions to get coordinates')
    console.warn('⚠️ No coordinates!')
    return
  }
  
  isLoading.value = true
  
  try {
    const stopData = {
      name: form.value.name,
      address: form.value.address,
      type: form.value.type as StopType,
      lat: form.value.lat,
      lng: form.value.lng,
      duration: form.value.duration,
      order: form.value.order
    }
    
    console.log('🎯 Emitting save event with:', stopData)
    emit('save', stopData)
  } finally {
    isLoading.value = false
  }
}
</script>